import { FC } from 'react';
import Grid from '@mui/material/Grid';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import useTranslations from '../global/translation/hooks/translation';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from '../global/ThemeContext';

const Pricing: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  // Debugging: Log translations to verify structure
  console.log('Translations object:', translations);

  // Explicitly typing currentLanguage to match the keys of translations.label
  type LanguageKeys = keyof typeof translations.label.price;
  const typedCurrentLanguage = currentLanguage as LanguageKeys;

  // Ensure translations.label.price and other properties are accessed correctly
  const exampleVals = {
    price: translations.label?.price?.[typedCurrentLanguage] || 'N/A',
    cena: translations.label?.price?.[typedCurrentLanguage] || 'N/A',
    zajeciaIndiwidulane: translations.label?.individualLessons?.[typedCurrentLanguage] || 'N/A',
    zajeciaWParze: translations.label?.pairedLessons?.[typedCurrentLanguage] || 'N/A',
    oneHr: translations.label?.oneHour?.[typedCurrentLanguage] || 'N/A',
    fourtyFive60Min: translations.label?.fourtyFiveToSixtyMinutes?.[typedCurrentLanguage] || 'N/A',
    fourtyFiveMin: translations.label?.fourtyFiveMinutes?.[typedCurrentLanguage] || 'N/A',
    fiftyMin: translations.label?.fiftyMinutes?.[typedCurrentLanguage] || 'N/A',
    halfHour: translations.label?.halfHour?.[typedCurrentLanguage] || 'N/A',
    seventyFiveMin: translations.label?.seventyFiveMinutes?.[typedCurrentLanguage] || 'N/A',
    description: translations.label?.description?.[typedCurrentLanguage] || 'N/A',
    training: translations.label?.training?.[typedCurrentLanguage] || 'N/A',
    trainingIndywidulane: translations.label?.individualTraining?.[typedCurrentLanguage] || 'N/A',
    trainingWParze: translations.label?.pairedTraining?.[typedCurrentLanguage] || 'N/A',
    trainingSkokowy: translations.label?.jumpTraining?.[typedCurrentLanguage] || 'N/A',
    trainingSkokowyIndywidulane: translations.label?.individualJumpTraining?.[typedCurrentLanguage] || 'N/A',
    trainingNaKoniuPrywatny: translations.label?.privateHorseTraining?.[typedCurrentLanguage] || 'N/A',
    lonza: translations.label?.lunge?.[typedCurrentLanguage] || 'N/A',
    zajeciaDlaNajmlodszych: translations.label?.youngestLessons?.[typedCurrentLanguage] || 'N/A',
    naukaLonzowaniaPracyZZiemii: translations.label?.lungingAndGroundWork?.[typedCurrentLanguage] || 'N/A',
    teren: translations.label?.trail?.[typedCurrentLanguage] || 'N/A',
    trailRide: translations.label?.trailRide?.[typedCurrentLanguage] || 'N/A',
    lesson: translations.label?.lesson?.[typedCurrentLanguage] || 'N/A',
    horseRental: translations.label?.horseRental?.[typedCurrentLanguage] || 'N/A',
    serwis: translations.label?.service?.[typedCurrentLanguage] || 'N/A',
    czasTrwania: translations.label?.duration?.[typedCurrentLanguage] || 'N/A',
    karnety: translations.label?.passes?.[typedCurrentLanguage] || 'N/A',
    pensjonat: translations.label?.pension?.[typedCurrentLanguage] || 'N/A',
    treiningKonia: translations.label?.horseTraining?.[typedCurrentLanguage] || 'N/A',
    otherServices: translations.label?.otherServices?.[typedCurrentLanguage] || 'N/A',
    boksWolnyWybieg: translations.label?.boxFreeRun?.[typedCurrentLanguage] || 'N/A',
    ustalanyIndywidualnie: translations.label?.individuallyDetermined?.[typedCurrentLanguage] || 'N/A',
    passDiscount: translations.label?.passDiscount?.[typedCurrentLanguage] || 'N/A',
  };

  return (
    <Grid my={6} py={2} container justifyContent="center" alignItems="center" sx={{ bgcolor: darkMode ? '#181818' : theme.palette.background.default }}>
      <Grid item xs={12} mb={3}>
        <Typography
          variant="h4"
          fontWeight={300}
          sx={{
            color: darkMode ? '#FFF' : '#000',
          }}
          textAlign="center"
        >
          {translations.menuItems.pricing[typedCurrentLanguage]}
        </Typography>
      </Grid>
      <Grid item xs={12} mx={2}>
        <Paper elevation={12} sx={{ p: 2, bgcolor: darkMode ? '#232323' : theme.palette.background.paper }}>
          <TableContainer component={Paper} sx={{ bgcolor: darkMode ? '#232323' : theme.palette.background.paper }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#FFF' : '#232323' }}>{exampleVals.serwis}</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.cena}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.trainingIndywidulane}</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>170 zł</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.trainingWParze}</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>120 zł</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.trainingNaKoniuPrywatny}</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>150 zł</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.lonza} (30min)</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>100 zł</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.teren}</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>150 zł</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
      <Grid item xs={12} mx={2} mt={2}>
        <Paper elevation={12} sx={{ p: 2, bgcolor: darkMode ? '#232323' : theme.palette.background.paper }}>
          <Typography
            variant="body1"
            sx={{
              color: darkMode ? '#FFF' : '#232323',
              textAlign: 'center',
            }}
          >
            {exampleVals.passDiscount}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} mx={2} mt={2}>
        <Paper elevation={12} sx={{ p: 2, bgcolor: darkMode ? '#232323' : theme.palette.background.paper }}>
          <TableContainer component={Paper} sx={{ bgcolor: darkMode ? '#232323' : theme.palette.background.paper }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="h6" sx={{ color: darkMode ? '#fff' : '#232323' }}>
                      {exampleVals.otherServices}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.serwis}</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.description}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.pensjonat}</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.ustalanyIndywidualnie}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.trainingSkokowyIndywidulane}</TableCell>
                  <TableCell sx={{ color: darkMode ? '#fff' : '#232323' }}>{exampleVals.ustalanyIndywidualnie}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Pricing;
